import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { loadBootstrapSession, loadDeferredBootstrapSession } from '../api/bootstrapApi';
import AppSplashScreen from '../components/AppSplashScreen';
import { applySessionPatch, normalizeSession } from '../session/bootstrapSessionCore.mjs';

const AppSessionContext = createContext(null);
const BOOTSTRAP_TIMEOUT_MS = 15000;

const BOOT_PHASES = {
  idle: { progress: 8, message: 'App wird gestartet …' },
  auth: { progress: 24, message: 'Anmeldung wird geprüft …' },
  request: { progress: 48, message: 'Spielstand wird vom Server geladen …' },
  hydrate: { progress: 82, message: 'Inventar, Expeditionen und Werft werden vorbereitet …' },
  ready: { progress: 100, message: 'Bereit zum Entern.' },
};

function getPhase(phase) {
  return BOOT_PHASES[phase] || BOOT_PHASES.idle;
}

export function AppSessionProvider({ children, enabled = true, initialSession = null }) {
  const initialSessionRef = useRef(initialSession ? normalizeSession(initialSession) : null);
  const hasInitialSession = Boolean(initialSessionRef.current);
  const [session, setSession] = useState(initialSessionRef.current);
  const [phase, setPhase] = useState('idle');
  const [loading, setLoading] = useState(Boolean(enabled && !hasInitialSession));
  const [ready, setReady] = useState(!enabled || hasInitialSession);
  const [error, setError] = useState(null);
  const [deferredLoading, setDeferredLoading] = useState(Boolean(enabled && hasInitialSession));
  const [deferredError, setDeferredError] = useState(null);
  const mountedRef = useRef(false);
  const loadIdRef = useRef(0);
  const blockingControllerRef = useRef(null);
  const deferredLoadIdRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) {
      setReady(true);
      setLoading(false);
      return null;
    }

    blockingControllerRef.current?.abort();
    const loadId = loadIdRef.current + 1;
    loadIdRef.current = loadId;
    const controller = new AbortController();
    blockingControllerRef.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), BOOTSTRAP_TIMEOUT_MS);

    setLoading(true);
    setReady(false);
    setError(null);
    setPhase('request');

    try {
      const payload = await loadBootstrapSession({ signal: controller.signal });
      if (loadIdRef.current !== loadId) return null;

      setPhase('hydrate');
      const normalized = normalizeSession(payload);
      setSession(normalized);
      setPhase('ready');
      setReady(true);
      setLoading(false);
      return normalized;
    } catch (loadError) {
      const nextError = loadError?.name === 'AbortError'
        ? new Error('Der Spielstand konnte nicht rechtzeitig geladen werden. Bitte erneut versuchen.')
        : loadError;

      setError(nextError);
      setReady(false);
      setLoading(false);
      setPhase('idle');
      return null;
    } finally {
      window.clearTimeout(timeout);
      if (blockingControllerRef.current === controller) {
        blockingControllerRef.current = null;
      }
    }
  }, [enabled]);

  useEffect(() => {
    mountedRef.current = true;

    if (!hasInitialSession) {
      load();
    }

    return () => {
      mountedRef.current = false;
      loadIdRef.current += 1;
      blockingControllerRef.current?.abort();
    };
  }, [hasInitialSession, load]);

  useEffect(() => {
    if (!enabled || !hasInitialSession) return undefined;

    const controller = new AbortController();
    const loadId = deferredLoadIdRef.current + 1;
    deferredLoadIdRef.current = loadId;
    const startTimer = window.setTimeout(async () => {
      setDeferredLoading(true);
      setDeferredError(null);
      try {
        const payload = await loadDeferredBootstrapSession({ signal: controller.signal });
        if (!mountedRef.current || deferredLoadIdRef.current !== loadId) return;
        setSession((previous) => applySessionPatch(previous, payload));
      } catch (loadError) {
        if (loadError?.name !== 'AbortError' && mountedRef.current && deferredLoadIdRef.current === loadId) {
          setDeferredError(loadError);
        }
      } finally {
        if (mountedRef.current && deferredLoadIdRef.current === loadId) {
          setDeferredLoading(false);
        }
      }
    }, 0);

    return () => {
      window.clearTimeout(startTimer);
      controller.abort();
      if (deferredLoadIdRef.current === loadId) {
        deferredLoadIdRef.current += 1;
      }
    };
  }, [enabled, hasInitialSession]);

  const updateSessionSlice = useCallback((sliceName, value) => {
    if (!sliceName) return;
    setSession((previous) => normalizeSession({ ...(previous || {}), [sliceName]: value }));
  }, []);

  const mergeSessionPatch = useCallback((patch) => {
    if (!patch || typeof patch !== 'object') return;
    setSession((previous) => applySessionPatch(previous, patch));
  }, []);

  const getSlice = useCallback((sliceName, fallback = null) => {
    if (!sliceName) return fallback;
    return session?.[sliceName] ?? fallback;
  }, [session]);

  const value = useMemo(() => {
    const phaseInfo = getPhase(phase);

    return {
      session,
      loading,
      ready,
      error,
      deferredLoading,
      deferredError,
      phase,
      progress: phaseInfo.progress,
      message: phaseInfo.message,
      reloadSession: load,
      updateSessionSlice,
      mergeSessionPatch,
      getSlice,
    };
  }, [session, loading, ready, error, deferredLoading, deferredError, phase, load, updateSessionSlice, mergeSessionPatch, getSlice]);

  if (!enabled) {
    return (
      <AppSessionContext.Provider value={value}>
        {children}
      </AppSessionContext.Provider>
    );
  }

  if (!ready || loading || error) {
    return (
      <AppSessionContext.Provider value={value}>
        <AppSplashScreen
          progress={value.progress}
          message={error ? 'Dein Spielstand konnte nicht geladen werden.' : value.message}
          error={error}
          onRetry={load}
        />
      </AppSessionContext.Provider>
    );
  }

  return (
    <AppSessionContext.Provider value={value}>
      {children}
    </AppSessionContext.Provider>
  );
}

export function useAppSession() {
  const context = useContext(AppSessionContext);
  if (!context) {
    throw new Error('useAppSession muss innerhalb des AppSessionProvider verwendet werden.');
  }
  return context;
}

export function useOptionalAppSession() {
  return useContext(AppSessionContext);
}

export default AppSessionContext;
